import { useTranslation } from 'react-i18next'
import { Button, Input } from '../../atoms'

interface RichTextLinkRowProps {
  id: string
  value: string
  error: string
  errorId: string
  // An existing link is being edited: it can also be removed
  editing: boolean
  inputRef: React.Ref<HTMLInputElement>
  onChange: (value: string) => void
  onApply: () => void
  onRemove: () => void
  onCancel: () => void
}

// The inline "link address" row of the editor. Presentational: the selection and the editing
// commands stay in RichTextField. Not memoized: it only renders while open, and its callbacks are
// new on every render of the field anyway.
export function RichTextLinkRow({
  id,
  value,
  error,
  errorId,
  editing,
  inputRef,
  onChange,
  onApply,
  onRemove,
  onCancel,
}: RichTextLinkRowProps) {
  const { t } = useTranslation('admin-mobifull')
  return (
    // biome-ignore lint/a11y/useSemanticElements: an inline row, not a fieldset
    <div className="rich-text__link" id={id} role="group" aria-label={t('richText.link')}>
      <Input
        ref={inputRef}
        type="text"
        inputMode="url"
        autoComplete="off"
        aria-label={t('richText.linkAddress')}
        placeholder={t('richText.linkPlaceholder')}
        value={value}
        error={!!error}
        aria-invalid={!!error}
        aria-describedby={error ? errorId : undefined}
        onChange={e => onChange(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            // Never submit the surrounding form
            e.preventDefault()
            onApply()
          } else if (e.key === 'Escape') {
            e.preventDefault()
            e.stopPropagation()
            onCancel()
          }
        }}
      />
      <Button type="button" onClick={onApply}>
        {t('richText.linkApply')}
      </Button>
      {editing ? (
        <Button type="button" variant="secondary" onClick={onRemove}>
          {t('richText.linkRemove')}
        </Button>
      ) : null}
      <Button type="button" variant="secondary" onClick={onCancel}>
        {t('richText.linkCancel')}
      </Button>
      {error ? (
        <p className="error-text rich-text__link-error" id={errorId} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  )
}
